import { Vec3 } from "../../../../models/SyntheticMap";

export interface ColorRGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface Index3D {
  x: number;
  y: number;
  z: number;
}

export interface MeshBlock {
  vertices: Vec3[];
  normals: Vec3[];
  colors: ColorRGBA[];
  triangles: number[]; // signed int32 indices
}

export interface ParsedNvbloxMesh {
  blockSize: number;
  blockIndices: Index3D[];
  blocks: MeshBlock[];
  clear: boolean;
}

/**
 * Parses an ArrayBuffer containing the custom-packed nvblox mesh format
 * (as produced by the Python node above, little-endian, no ROS header)
 */
export function parseNvbloxMeshBuffer(buffer: ArrayBuffer): ParsedNvbloxMesh {
  const view = new DataView(buffer);
  let offset = 0;

  // Helper functions for reading
  const readFloat32 = (): number => {
    const val = view.getFloat32(offset, true); // little-endian
    offset += 4;
    return val;
  };

  const readInt32 = (): number => {
    const val = view.getInt32(offset, true);
    offset += 4;
    return val;
  };

  const readUint32 = (): number => {
    const val = view.getUint32(offset, true);
    offset += 4;
    return val;
  };

  const readUint8 = (): number => {
    const val = view.getUint8(offset);
    offset += 1;
    return val;
  };

  const readVector3 = (): Vec3 => ({
    x: readFloat32(),
    y: readFloat32(),
    z: readFloat32(),
  });

  const readColorRGBA = (): ColorRGBA => ({
    r: readFloat32(),
    g: readFloat32(),
    b: readFloat32(),
    a: readFloat32(),
  });

  const readIndex3D = (): Index3D => ({
    x: readInt32(),
    y: readInt32(),
    z: readInt32(),
  });

  // --- Start parsing ---

  // 1. block_size: float32
  const blockSize = readFloat32();

  // 2. block_indices count + array
  const blockIndicesCount = readUint32();
  const blockIndices: Index3D[] = [];
  for (let i = 0; i < blockIndicesCount; i++) {
    blockIndices.push(readIndex3D());
  }

  // 3. blocks count + array of MeshBlock
  const blocksCount = readUint32();
  const blocks: MeshBlock[] = [];

  for (let b = 0; b < blocksCount; b++) {
    // vertices
    const vertexCount = readUint32();
    const vertices: Vec3[] = [];
    for (let i = 0; i < vertexCount; i++) {
      vertices.push(readVector3());
    }
    /*
    // normals
    const normalCount = readUint32();
    const normals: Vec3[] = [];
    for (let i = 0; i < normalCount; i++) {
      normals.push(readVector3());
    }

    // colors
    const colorCount = readUint32();
    const colors: ColorRGBA[] = [];
    for (let i = 0; i < colorCount; i++) {
      colors.push(readColorRGBA());
    }
    */

    // triangles (int32[])
    const triangleCount = readUint32();
    const triangles: number[] = [];
    for (let i = 0; i < triangleCount; i++) {
      triangles.push(readInt32());
    }

    blocks.push({
      vertices,
      normals: [],
      colors: [],
      triangles,
    });
  }

  // 4. clear: uint8 as boolean
  // const clear = readUint8() !== 0;

  // Optional: check if we've consumed the entire buffer
  if (offset !== buffer.byteLength) {
    console.warn(
      `Warning: Buffer parsing incomplete. Expected ${buffer.byteLength} bytes, consumed ${offset}.`
    );
  }

  return {
    blockSize,
    blockIndices,
    blocks,
    clear: false,
  };
}